import 'bootstrap';

// Fetch Vimeo thumbnails client-side via the public oEmbed API (no API key
// needed, CORS-accessible). Finds every .video-thumbnail button for a Vimeo
// video that has no <img> inside it (i.e. no custom thumbnail was set), fetches
// the thumbnail URL, and injects the <img> so the play button has a background.
async function loadVimeoThumbnails(): Promise<void> {
  const buttons = document.querySelectorAll<HTMLButtonElement>(
    '.video-thumbnail[data-video-host="vimeo"]'
  );

  const fetches = Array.from(buttons)
    .filter(btn => !btn.querySelector('img'))
    .map(async btn => {
      const videoId = btn.dataset.videoId;
      if (!videoId) return;
      try {
        const res = await fetch(
          `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${encodeURIComponent(videoId)}&width=640`
        );
        if (!res.ok) return;
        const data = await res.json();
        const thumbnailUrl: string = data.thumbnail_url;
        if (!thumbnailUrl) return;
        const img = document.createElement('img');
        img.className = 'video-thumbnail-image';
        img.src = thumbnailUrl;
        img.alt = btn.getAttribute('aria-label') || '';
        // Insert before the play button span so it sits behind it
        const playBtn = btn.querySelector('.video-thumbnail-play-button');
        btn.insertBefore(img, playBtn);
      } catch {
        // oEmbed failed — play button still works, just no thumbnail image
      }
    });

  await Promise.allSettled(fetches);
}

function handleVideoModalEvents(): void {
  const videoModalElement = document.getElementById('videoModal');
  const videoModalContentElement = document.getElementById('videoModalContent');
  const videoModalLabelElement = document.getElementById('videoModalLabel');

  if (videoModalElement && videoModalContentElement) {
    videoModalElement.addEventListener('show.bs.modal', (event: Event & { relatedTarget: HTMLElement }) => {
      const relatedTarget = event.relatedTarget;
      if (relatedTarget) {
        switch (relatedTarget.dataset.videoHost) {
          case 'youtube':
            const iframeTitle = relatedTarget.dataset.videoTitle || 'YouTube video player';
            if (relatedTarget.dataset.videoPlaylist === "true") {
              videoModalContentElement.innerHTML = `<iframe src="https://www.youtube.com/embed/videoseries?list=${relatedTarget.dataset.videoId}&controls=0&autoplay=1" title="${iframeTitle}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
            } else {
              videoModalContentElement.innerHTML = `<iframe src="https://www.youtube.com/embed/${relatedTarget.dataset.videoId}?controls=0&autoplay=1" title="${iframeTitle}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
            }
            break;
          case 'vimeo': {
            const vimeoTitle = relatedTarget.dataset.videoTitle || 'Vimeo video player';
            videoModalContentElement.innerHTML = `<iframe src="https://player.vimeo.com/video/${relatedTarget.dataset.videoId}?autoplay=1&title=0&byline=0&portrait=0" title="${vimeoTitle}" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`;
            break;
          }
          default:
            console.warn(`Video host '${relatedTarget.dataset.videoHost}' not supported.`);
            break;
        }
        videoModalLabelElement.innerHTML = relatedTarget.attributes['aria-label'].value
      }
    });

    videoModalElement.addEventListener('hidden.bs.modal', () => {
      videoModalContentElement.innerHTML = '';
      videoModalLabelElement.innerHTML = '';
    });
  }
}

// In page builder edit mode, Kentico AJAX-replaces widget markup when properties
// are saved, wiping any client-injected Vimeo thumbnails. Re-run whenever new
// Vimeo video buttons appear in the DOM.
function watchForWidgetRerenders(): void {
  const observer = new MutationObserver((mutations) => {
    const hasNewVimeoButton = mutations.some((m) =>
      Array.from(m.addedNodes).some(
        (node) =>
          node instanceof Element &&
          (node.matches('.video-thumbnail[data-video-host="vimeo"]') ||
            node.querySelector('.video-thumbnail[data-video-host="vimeo"]') !== null)
      )
    );
    if (hasNewVimeoButton) loadVimeoThumbnails();
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

document.addEventListener('DOMContentLoaded', () => {
  loadVimeoThumbnails();
  handleVideoModalEvents();
  watchForWidgetRerenders();
});
